import type { EventRow } from "@/lib/api/events";

export type EventLifecycleState =
  | "scheduled"
  | "pre_reveal"
  | "live"
  | "wrapping_up"
  | "completed"
  | "cancelled";

export interface EventTiming {
  state: EventLifecycleState;
  revealAtMs: number;
  eventStartMs: number;
  eventEndMs: number;
  wrappingUpEndMs: number;
  msToReveal: number;
  msToEventStart: number;
  msSinceEventEnd: number;
}

const HOUR = 3_600_000;
const PRE_REVEAL_WINDOW = 6 * HOUR;
const ASSUMED_EVENT_DURATION = 3 * HOUR;
const POST_EVENT_BUFFER = 2 * HOUR;
const WRAPPING_UP_WINDOW = 24 * HOUR;

export function computeEventTiming(
  event: Pick<EventRow, "event_date" | "match_reveal_hours" | "status">,
  now: number = Date.now(),
): EventTiming {
  const eventStartMs = new Date(event.event_date).getTime();
  const revealAtMs = eventStartMs - event.match_reveal_hours * HOUR;
  const eventEndMs = eventStartMs + ASSUMED_EVENT_DURATION + POST_EVENT_BUFFER;
  const wrappingUpEndMs = eventEndMs + WRAPPING_UP_WINDOW;

  let state: EventLifecycleState;
  if (event.status === "cancelled") state = "cancelled";
  else if (event.status === "completed") state = "completed";
  else if (now >= wrappingUpEndMs) state = "completed";
  else if (now >= eventEndMs) state = "wrapping_up";
  else if (now >= eventStartMs) state = "live";
  else if (now >= revealAtMs) state = "live";
  else if (revealAtMs - now <= PRE_REVEAL_WINDOW) state = "pre_reveal";
  else state = "scheduled";

  return {
    state,
    revealAtMs,
    eventStartMs,
    eventEndMs,
    wrappingUpEndMs,
    msToReveal: revealAtMs - now,
    msToEventStart: eventStartMs - now,
    msSinceEventEnd: now - eventEndMs,
  };
}

export function formatHms(ms: number): { h: string; m: string; s: string } {
  if (ms <= 0) return { h: "00", m: "00", s: "00" };
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  return {
    h: String(h).padStart(2, "0"),
    m: String(m).padStart(2, "0"),
    s: String(s).padStart(2, "0"),
  };
}

export function formatDurationLong(ms: number): string {
  if (ms <= 0) return "0m";
  const totalMin = Math.floor(ms / 60_000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}
